// customFetch.js
'use strict';
import 'isomorphic-fetch';

import { customFetch as customFetchLog } from '../resource/debug.js';
import { reduxStoreInitializePromise } from '../resource/reduxStoreInitializePromise.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import responseDecryptionUtil from '../resource/responseDecryptionUtil.js';

const log = customFetchLog.extend('log');
const errorLog = customFetchLog.extend('error');

// Inline QQ Browser detection so customFetch.js does not import
// getUserAgent.js, which transitively pulls env.js — that file reads
// process.env.TAG_NAME / BRANCH_NAME / SHORT_SHA at module top and would
// otherwise leak per-build values into the Service Worker bundle (causing
// SW chunkhash drift on every Docker build).
const isQqBrowser = /QQBrowser|MQQBrowser|QBWebViewType|QQ\//i.test(
  globalThis.navigator?.userAgent || ''
);

let tokenRenewer = null;

/**
 * Set token renewer to customFetch
 * @param {function} {tokenRenewer} - function to pre-check and update header access token
 * @param {bool} {[shouldReplace = false]} - whether should replace old renewer
 */
export const setTokenRenewer = ({
  tokenRenewer: inputTokenRenewer,
  shouldReplace = false,
}) => {
  if (shouldReplace || !tokenRenewer) {
    tokenRenewer = inputTokenRenewer;
    log('setTokenRenewer', { inputTokenRenewer, shouldReplace });
  }
};

/**
 * Custom fetch
 * @param {string} url - regular url param for native fetch.
 * @param {object} options - regular options param for native fetch.
 */
const customFetch = async (inputUrl, inputOptions) => {
  let url = inputUrl;
  let options = inputOptions;
  const shouldUseCustomFetch = inputOptions?.shouldUseCustomFetch !== false;

  if (!shouldUseCustomFetch) {
    log('custom fetch bypassed');

    return fetch(url, options);
  }

  if (options?.headers) {
    // input may be object style or Headers object
    const headers = new Headers(options?.headers);
    options.headers = headers;
  }

  log('init', {
    inputUrl,
    inputOptions,
    options,
  });

  // Pre-process request

  if (inputOptions?.shouldBypassPreProcess) {
    log('custom fetch bypassed pre-process');
  } else {
    for (let i = 0; i < customFetch.usePreProcessRequest.length; i += 1) {
      const preProcess = customFetch.usePreProcessRequest[i];
      log('preProcess start', preProcess.name, { url, options });
      const result = await preProcess({ url, options });
      log('preProcess done', preProcess.name, { url, options });

      url = result.url;
      options = result.options;
    }
  }

  // Execute fetch
  let response = await fetch(url, options);

  // Post-process response
  if (inputOptions?.shouldBypassPostProcess) {
    log('custom fetch bypassed post-process');
  } else {
    for (let i = 0; i < customFetch.usePostProcessResponse.length; i += 1) {
      const postProcess = customFetch.usePostProcessResponse[i];
      log('postProcess start', postProcess.name, { url });
      const result = await postProcess({ response });
      log('postProcess done', postProcess.name, { url });
      response = result.response;
    }
  }

  return response;
};

const checkXPrefixHeader = async ({ url, options }) => {
  // only for api.swag.live
  try {
    const fullUrl = new URL(url);
    if (fullUrl.hostname !== 'api.swag.live') return { url, options };
    // eslint-disable-next-line no-empty
  } catch (_) {}

  if (
    options?.headers &&
    (!options.headers.has('x-client-id') || !options.headers.has('x-track'))
  ) {
    // for Next.js, we need to wait for the redux store is initialized
    await reduxStoreInitializePromise;

    options.headers = new Headers({
      ...Object.fromEntries(options.headers),
      ...getHeaders(),
    });
  }

  return { url, options };
};

const checkAuthorizationHeader = async ({ url, options }) => {
  // for *.swag.live
  try {
    const fullUrl = new URL(url);
    if (!/swag.live$/gi.test(fullUrl.hostname)) return { url, options };
    // eslint-disable-next-line no-empty
  } catch (_) {}

  const authorizationHeader = options?.headers?.get('Authorization');

  if (tokenRenewer && authorizationHeader) {
    const { url: newUrl, options: newOptions } = await tokenRenewer({
      url,
      options,
    });

    return { url: newUrl, options: newOptions };
  }
  return { url, options };
};

customFetch.usePreProcessRequest = [
  checkXPrefixHeader,
  checkAuthorizationHeader,
];

const checkIsTextLikeContentType = contentType => {
  if (!contentType) return false;

  const [mime] = contentType.split(';');
  const type = mime.trim().toLowerCase();

  if (type.startsWith('text/')) return true;

  if (
    type === 'application/json' ||
    type === 'application/ld+json' ||
    type === 'application/javascript' ||
    type === 'application/x-javascript' ||
    type === 'application/xml' ||
    type === 'application/xhtml+xml' ||
    type === 'application/x-www-form-urlencoded'
  ) {
    return true;
  }

  if (type.endsWith('+json') || type.endsWith('+xml')) return true;

  return false;
};

const handleEncryptedResponse = async ({ response }) => {
  const hasEncryptionHeaders =
    response.headers.get('x-encrypted-iv') &&
    response.headers.get('x-encrypted-algo') &&
    response.headers.get('x-encrypted-key-id');

  if (!hasEncryptionHeaders) {
    log('handleEncryptedResponse() no encryption headers found');
    return { response };
  }

  try {
    log('handleEncryptedResponse() decrypting response');

    const newHeaders = new Headers(response.headers);

    // remove encryption headers to avoid responseDecryptionUtil() to decrypt again
    newHeaders.delete('x-encrypted-iv');
    newHeaders.delete('x-encrypted-algo');
    newHeaders.delete('x-encrypted-key-id');

    const rawContentType = response.headers.get('x-content-type');
    if (rawContentType) {
      newHeaders.set('content-type', rawContentType);
    } else {
      throw new Error('content-type is required for encrypted response');
    }

    let decryptedResponse = null;

    const isTextLikeContentType = checkIsTextLikeContentType(rawContentType);
    // For QQ browser, decode the response first to avoid qq browser's default incorrect parsing
    const needsPreDecode = isQqBrowser && isTextLikeContentType;
    const { stream, buffer, contentType } = await responseDecryptionUtil({
      response,
      preferredReturnType:
        needsPreDecode && !globalThis.TextDecoderStream
          ? 'arrayBuffer'
          : 'readableStream',
    });

    log('handleEncryptedResponse() decryption result', {
      hasStream: !!stream,
      hasBuffer: !!buffer,
      bufferByteLength: buffer?.byteLength,
      needsPreDecode,
    });

    if (needsPreDecode) {
      const rawCharset = contentType.match(/charset=(\S+)/i)?.[1];
      const charset = rawCharset || 'utf-8';
      if (!rawCharset) {
        newHeaders.set('content-type', `${contentType}; charset=${charset}`);
      }

      let decodedPayload = null;
      if (stream) {
        decodedPayload = stream.pipeThrough(new TextDecoderStream(charset));
      } else {
        decodedPayload = new TextDecoder(charset).decode(buffer);
      }

      decryptedResponse = new Response(decodedPayload, {
        status: response.status,
        statusText: response.statusText,
        headers: newHeaders,
      });
    } else {
      decryptedResponse = new Response(stream || buffer, {
        status: response.status,
        statusText: response.statusText,
        headers: newHeaders,
      });
    }

    // Copy read-only Response properties that cannot be set via constructor
    Object.defineProperty(decryptedResponse, 'url', {
      value: response.url,
      writable: false,
      enumerable: true,
      configurable: false,
    });

    Object.defineProperty(decryptedResponse, 'redirected', {
      value: response.redirected,
      writable: false,
      enumerable: true,
      configurable: false,
    });

    Object.defineProperty(decryptedResponse, 'type', {
      value: response.type,
      writable: false,
      enumerable: true,
      configurable: false,
    });

    log('handleEncryptedResponse() response decrypted successfully');
    return { response: decryptedResponse };
  } catch (error) {
    errorLog('handleEncryptedResponse() decryption failed', { error });
    return { response };
  }
};

customFetch.usePostProcessResponse = [handleEncryptedResponse];

export default customFetch;
